import React, { useState, useEffect } from 'react';
import { 
  FiGlobe, FiFileText, FiCalendar, FiUsers, 
  FiShield, FiBookOpen, FiImage, FiSave, FiCheck 
} from 'react-icons/fi';
import ImageUploadField from './ImageUploadField';
import AdminSpeakers from './AdminSpeakers';
import AdminPartners from './AdminPartners';
import AdminPublications from './AdminPublications';
import AdminGallery from './AdminGallery';
import './AdminManage.css';

const STORAGE_KEY_GSIDA = 'iamar_gsida_general_content';

const DEFAULT_GSIDA_DATA = {
  presentation: {
    title: "Congrès International GSIDA 2026",
    subtitle: "Gouvernance, Sécurité, Innovation et Développement en Afrique",
    dates: "28 - 30 Octobre 2026",
    location: "Palais des Congrès, Agadir - Maroc",
    bannerImage: "",
    description: "Le Congrès GSIDA réunit décideurs publics, leaders industriels, universitaires et experts internationaux pour échanger sur les grands défis sécuritaires et les leviers d'innovation technologique sur le continent africain."
  },
  theme: {
    mainTheme: "Gouvernance Sécuritaire et Souveraineté Numérique à l'Ère de l'Intelligence Artificielle",
    tagline: "Construire des modèles résilients et éthiques pour le développement africain",
    axisList: [
      "Cybersécurité et résilience des infrastructures critiques",
      "Intelligence artificielle et transformation des services de sécurité",
      "Coopération transfrontalière et gouvernance des données"
    ]
  },
  program: {
    daysCount: 3,
    sessionsNote: "Programme articulé autour de conférences plénières, tables rondes ministérielles et ateliers techniques spécialisés."
  }
};

export default function AdminGSIDASection() {
  const [activeSubTab, setActiveSubTab] = useState('presentation');
  const [data, setData] = useState(DEFAULT_GSIDA_DATA);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY_GSIDA);
      if (saved) {
        setData(JSON.parse(saved));
      }
    } catch (e) {
      console.error(e);
    }
  }, []);

  const handleSave = () => {
    localStorage.setItem(STORAGE_KEY_GSIDA, JSON.stringify(data));
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 2500);
  };

  const subTabs = [
    { id: 'presentation', label: "Présentation Congrès", icon: <FiFileText /> },
    { id: 'theme', label: "Thème 2026", icon: <FiGlobe /> },
    { id: 'program', label: "Programme", icon: <FiCalendar /> },
    { id: 'speakers', label: "Intervenants (Speakers)", icon: <FiUsers /> },
    { id: 'partners', label: "Partenaires GSIDA", icon: <FiShield /> },
    { id: 'publications', label: "Publications GSIDA", icon: <FiBookOpen /> },
    { id: 'gallery', label: "Galerie Média", icon: <FiImage /> }
  ];

  return (
    <div className="admin-manage-card" style={{ background: '#ffffff', borderRadius: 8, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0', paddingBottom: 16, marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.4rem', color: '#1b1e4f', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 10 }}>
            <FiGlobe /> Gestion du Congrès GSIDA
          </h2>
          <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '0.88rem' }}>
            Gestion centralisée de toutes les composantes du congrès international GSIDA
          </p>
        </div>

        {['presentation', 'theme', 'program'].includes(activeSubTab) && (
          <button
            onClick={handleSave}
            style={{
              background: saveSuccess ? '#10b981' : '#1b1e4f',
              color: 'white',
              border: 'none',
              padding: '9px 20px',
              borderRadius: 6,
              fontWeight: 600,
              fontSize: 13,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}
          >
            {saveSuccess ? <FiCheck size={16} /> : <FiSave size={16} />}
            {saveSuccess ? 'Enregistré avec succès !' : 'Enregistrer les modifications'}
          </button>
        )}
      </div>

      {/* Sub Tabs */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 24, padding: 6, background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
        {subTabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveSubTab(tab.id)}
            style={{
              border: 'none',
              background: activeSubTab === tab.id ? '#1b1e4f' : 'transparent',
              color: activeSubTab === tab.id ? '#ffffff' : '#475569',
              padding: '8px 14px',
              borderRadius: 6,
              fontSize: 12.5,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              transition: 'all 0.15s ease'
            }}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Sub-tab: Présentation */}
      {activeSubTab === 'presentation' && (
        <div style={{ maxWidth: 840 }}>
          <h3 style={{ fontSize: '1.15rem', color: '#1e293b', marginBottom: 16 }}>
            Présentation du Congrès GSIDA
          </h3>

          <ImageUploadField
            label="Bannière d'affiche officielle du Congrès (Upload image)"
            value={data.presentation.bannerImage}
            onChange={(url) => setData(prev => ({ ...prev, presentation: { ...prev.presentation, bannerImage: url } }))}
            helpText="Téléversez l'affiche ou visuel officiel de l'édition GSIDA"
          />

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>Titre de l'événement</label>
              <input
                type="text"
                value={data.presentation.title}
                onChange={(e) => setData(prev => ({ ...prev, presentation: { ...prev.presentation, title: e.target.value } }))}
                style={{ width: '100%', padding: '9px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13 }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>Dates de l'événement</label>
              <input
                type="text"
                value={data.presentation.dates}
                onChange={(e) => setData(prev => ({ ...prev, presentation: { ...prev.presentation, dates: e.target.value } }))}
                style={{ width: '100%', padding: '9px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13 }}
              />
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>Lieu / Ville</label>
            <input
              type="text"
              value={data.presentation.location}
              onChange={(e) => setData(prev => ({ ...prev, presentation: { ...prev.presentation, location: e.target.value } }))}
              style={{ width: '100%', padding: '9px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13 }}
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>Description introductive</label>
            <textarea
              rows={4}
              value={data.presentation.description}
              onChange={(e) => setData(prev => ({ ...prev, presentation: { ...prev.presentation, description: e.target.value } }))}
              style={{ width: '100%', padding: '9px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13 }}
            />
          </div>
        </div>
      )}

      {/* Sub-tab: Thème 2026 */}
      {activeSubTab === 'theme' && (
        <div style={{ maxWidth: 840 }}>
          <h3 style={{ fontSize: '1.15rem', color: '#1e293b', marginBottom: 16 }}>
            Thématique Centrale GSIDA 2026
          </h3>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>Intitulé du Thème Principal</label>
            <input
              type="text"
              value={data.theme.mainTheme}
              onChange={(e) => setData(prev => ({ ...prev, theme: { ...prev.theme, mainTheme: e.target.value } }))}
              style={{ width: '100%', padding: '9px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13 }}
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>Slogan / Sous-thématique</label>
            <textarea
              rows={2}
              value={data.theme.tagline}
              onChange={(e) => setData(prev => ({ ...prev, theme: { ...prev.theme, tagline: e.target.value } }))}
              style={{ width: '100%', padding: '9px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13 }}
            />
          </div>
        </div>
      )}

      {/* Sub-tab: Programme */}
      {activeSubTab === 'program' && (
        <div style={{ maxWidth: 840 }}>
          <h3 style={{ fontSize: '1.15rem', color: '#1e293b', marginBottom: 16 }}>
            Programme & Agenda du Congrès
          </h3>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>Note générale sur l'agenda</label>
            <textarea
              rows={3}
              value={data.program.sessionsNote}
              onChange={(e) => setData(prev => ({ ...prev, program: { ...prev.program, sessionsNote: e.target.value } }))}
              style={{ width: '100%', padding: '9px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13 }}
            />
          </div>
        </div>
      )}

      {/* Sub-tab: Speakers */}
      {activeSubTab === 'speakers' && <AdminSpeakers />}

      {/* Sub-tab: Partenaires */}
      {activeSubTab === 'partners' && <AdminPartners />}

      {/* Sub-tab: Publications */}
      {activeSubTab === 'publications' && <AdminPublications />}

      {/* Sub-tab: Galerie */}
      {activeSubTab === 'gallery' && <AdminGallery />}
    </div>
  );
}
